// Client for the generic site settings API (api/site_settings.php).

import { parseJsonResponse } from './apiFetch';
const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';

export const HERO_SLIDE_HOLD_SECONDS_KEY = 'hero_slide_hold_seconds';
export const DEFAULT_HERO_SLIDE_HOLD_SECONDS = 7;

// '0' disables the first-visit loading screen entirely; any other value
// (including the key being absent) keeps the existing always-on behavior.
export const LOADING_SCREEN_ENABLED_KEY = 'loading_screen_enabled';

// '0' hides the floating WhatsApp chat button entirely; any other value
// (including the key being absent) keeps it showing — same convention as
// LOADING_SCREEN_ENABLED_KEY. It also only renders when a WhatsApp number
// is configured (social_whatsapp), regardless of this flag.
export const WHATSAPP_BUTTON_ENABLED_KEY = 'whatsapp_button_enabled';
export const WHATSAPP_BUTTON_TEXT_KEY = 'whatsapp_button_text';
export const DEFAULT_WHATSAPP_BUTTON_TEXT =
  "Hi! I'm interested in booking a stay at Homeland Stay — could you help me with availability and pricing?";

// '0' hides the trust-badge embed (e.g. Trustindex) entirely; any other
// value (including the key being absent) keeps it showing — same
// convention as the other *_ENABLED_KEY flags. It also only renders when
// TRUST_BADGE_SCRIPT_KEY has content, regardless of this flag.
export const TRUST_BADGE_ENABLED_KEY = 'trust_badge_enabled';
export const TRUST_BADGE_SCRIPT_KEY = 'trust_badge_script';

// Sticky announcement bar above the nav header. '0' (or the key being
// absent) hides it — same convention as the other *_ENABLED_KEY flags. It
// also only renders when ANNOUNCEMENT_BANNER_TEXT_KEY has content,
// regardless of this flag.
export const ANNOUNCEMENT_BANNER_ENABLED_KEY = 'announcement_banner_enabled';
export const ANNOUNCEMENT_BANNER_TEXT_KEY = 'announcement_banner_text';
export const ANNOUNCEMENT_BANNER_TEXT_MAX_LENGTH = 140;
// 'gold_shimmer' reuses the same shimmering gold gradient as the Gujarat
// Tourism Gold Category trust-stat card (see index.css .hs-gold); 'solid'
// uses ANNOUNCEMENT_BANNER_COLOR_KEY as a flat background instead.
export const ANNOUNCEMENT_BANNER_STYLE_KEY = 'announcement_banner_style';
export const ANNOUNCEMENT_BANNER_COLOR_KEY = 'announcement_banner_color';
export const DEFAULT_ANNOUNCEMENT_BANNER_COLOR = '#4b5e28';

// The year shown in "hosted by the Jani family since <year>" in the footer.
export const HOSTING_SINCE_YEAR_KEY = 'hosting_since_year';
export const DEFAULT_HOSTING_SINCE_YEAR = '2020';

export async function fetchSiteSettings(bustCache = false): Promise<Record<string, string>> {
  // bustCache: see the matching comment on fetchRooms() in roomsApi.ts.
  const qs = bustCache ? `&v=${Date.now()}` : '';
  const res = await fetch(`${API_BASE}/site_settings.php?action=list${qs}`);
  const data = await parseJsonResponse(res);
  if (!data.success) throw new Error(data.message ?? 'Failed to load settings');
  return data.settings;
}

export async function setSiteSetting(
  key: string,
  value: string,
  csrfToken: string,
): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/site_settings.php?action=set`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, value, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}
